import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useTimeout } from '../../hooks/useTimeout';
import { IStrategy } from '../../types/model';
import { StrategyLabel } from './StrategyLabel';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import { Icon, PopperComponent } from '../UI';

const StrategySearch = ({
  className = '',
  strategies = [],
  selectedStrategies = [],
  setSelectedStrategies,
}: {
  className?: string;
  strategies: IStrategy[];
  selectedStrategies?: IStrategy[];
  setSelectedStrategies: React.Dispatch<React.SetStateAction<IStrategy[]>>;
}) => {
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<IStrategy[]>(strategies);

  const inputElement = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { t } = useTranslation();

  useOutsideClick(containerRef, () => {
    setIsDropdownVisible(false);
  });

  const toggleDropdown = () => setIsDropdownVisible((isVisible) => !isVisible);

  const onDropdownClick = (e) => {
    if (e.currentTarget !== e.target) {
      return;
    }

    toggleDropdown();
    setTimeout(() => inputElement?.current?.focus(), 0);
  };

  const { value, setValue } = useTimeout(() => {
    if (!value) {
      setSearchResults(strategies);
    } else {
      setSearchResults(
        strategies.filter((s) =>
          s.name.toLocaleLowerCase().includes(value.toLocaleLowerCase())
        )
      );
    }
  }, 300);

  return (
    <div ref={containerRef}>
      <PopperComponent
        sameWidth
        options={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 0],
              },
            },
          ],
        }}
        reference={
          <div className="flex items-center">
            <div
              className="text-black border border-grey-10 pt-3 pr-3 flex-grow-1 dark:border-grey-80 dark:text-white"
              onClick={onDropdownClick}
            >
              {selectedStrategies.length ? (
                selectedStrategies.map((strategy, index) => (
                  <StrategyLabel
                    key={strategy.id}
                    color={strategy.color}
                    name={strategy.name}
                    className={'ml-3 mb-3'}
                    viewType={'tag'}
                    onClose={() => {
                      setSelectedStrategies(
                        selectedStrategies.filter((s) => s.id !== strategy.id)
                      );
                    }}
                  />
                ))
              ) : (
                <div
                  className={'mb-3 ml-3 dark:text-white'}
                  onClick={onDropdownClick}
                >
                  {t('strategySearch.choose')}
                </div>
              )}
            </div>
          </div>
        }
        visible={isDropdownVisible}
      >
        <div
          className={
            'bg-white w-full border border-grey-30 rounded-lg shadow-xl dark:bg-grey-90 dark:border-grey-80'
          }
        >
          <div
            className={`flex w-full justify-between items-center p-4 border-b border-grey-10 dark:border-grey-80`}
          >
            <Icon svgName={'search'} />
            <input
              type="text"
              placeholder={t('strategySearch.search')}
              ref={inputElement}
              className="pl-1 text-black flex-grow-1 dark:bg-grey-90 dark:text-white"
              autoComplete={'off'}
              value={value || ''}
              onChange={({ target: { value = '' } }) => setValue(value)}
            />
            <Icon
              svgName={'close'}
              className={
                'bg-grey-30 text-white rounded-full w-4 h-4 p-1 cursor-pointer'
              }
              onClick={() => {
                if (value === '') {
                  setIsDropdownVisible(false);
                } else {
                  setValue('');
                }
              }}
            />
          </div>
          <div className={'px-2 pb-2 py-1 min-h-48 overflow-y-auto'}>
            {searchResults.length ? (
              searchResults.map((strategy) => (
                <div
                  key={strategy.id}
                  className={
                    'flex items-center p-2 rounded-lg justify-between my-1 cursor-pointer hover:bg-primary group'
                  }
                  onClick={() => {
                    if (selectedStrategies.includes(strategy)) {
                      setSelectedStrategies(
                        selectedStrategies.filter((s) => s !== strategy)
                      );
                    } else {
                      setSelectedStrategies([...selectedStrategies, strategy]);
                    }

                    toggleDropdown();
                  }}
                >
                  <StrategyLabel
                    color={strategy.color}
                    className={
                      'dark:bg-grey-90 dark:text-white group-hover:bg-primary dark:group-hover:text-black'
                    }
                    name={strategy.name}
                    viewType={'simple'}
                    size={'base'}
                  />
                  {selectedStrategies.find((s) => s.id === strategy.id) && (
                    <Icon svgName={'check'} />
                  )}
                </div>
              ))
            ) : (
              <div className={'text-sm pl-2 pt-2 dark:text-white'}>
                {t('strategySearch.noResults')}
              </div>
            )}
          </div>
        </div>
      </PopperComponent>
    </div>
  );
};

export default StrategySearch;
